import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../lib/i18n';
import useDocumentTitle from '../lib/useDocumentTitle';
import Footer from '../components/Footer';
import { ArrowLeft, Home, HelpCircle, ChevronDown } from 'lucide-react';

/**
 * Static FAQ page. 10 questions covering the most common pre-purchase
 * and onboarding objections. Public route, no auth needed.
 *
 * Why native `<details>` instead of a React accordion:
 *   - Zero JS to ship for the toggle behavior
 *   - Accessible by default (keyboard, screen readers)
 *   - All Q&A text is in the rendered HTML, so search engines + content
 *     summarizers see it directly without running our SPA
 *   - Works even if React fails to hydrate
 *
 * Content stored inline in DE + EN inside this file rather than going
 * through the i18n dict. Translation keys for Q&A blocks would clutter
 * the dict and the FAQ doesn't need to share strings with anything.
 */

const FAQ_ITEMS = {
  de: [
    {
      q: 'Wie funktioniert Real Content?',
      a: (
        <>
          <p>
            Du verbindest deinen Justimmo-Account einmalig. Wir importieren
            deine aktiven Inserate und du wählst aus, welche Vorlagen dir gefallen.
            Anschließend posten wir automatisch. Ein Video pro Tag, vollständig
            generiert mit KI-Voiceover, Musik und deinem Branding.
          </p>
          <p>
            Du musst nichts mehr tun: Ein neues Inserat in Justimmo? Wir
            erstellen daraus innerhalb von 12 Stunden ein Social-Media-Video,
            schicken dir eine Vorschau, und posten zur geplanten Zeit.
          </p>
        </>
      ),
    },
    {
      q: 'Brauche ich einen Justimmo-Account?',
      a: (
        <>
          <p>
            Aktuell ja. Real Content holt deine Immobilieninserate über die
            Justimmo Export-API. Damit funktioniert das Auto-Posting ohne
            zusätzliche manuelle Arbeit.
          </p>
          <p>
            Andere Maklersysteme (immowelt, ImmoScout24-Manager, etc.)
            unterstützen wir bislang nicht direkt. Wenn du eines davon nutzt
            und Real Content trotzdem testen willst, schreib uns kurz an
            {' '}<a className="text-[#1f4d3a] underline" href="mailto:support@real-content.at">support@real-content.at</a>{' '}
           . Wir suchen nach einer Lösung.
          </p>
        </>
      ),
    },
    {
      q: 'Welche Social-Media-Plattformen werden unterstützt?',
      a: (
        <p>
          Sechs Plattformen out-of-the-box: <strong>Instagram, Facebook,
          TikTok, YouTube (Shorts), X (Twitter)</strong> und{' '}
          <strong>LinkedIn</strong>. Du verbindest jede einmal über
          Upload-Post, danach posten wir parallel auf alle. In den
          Auto-Post-Einstellungen kannst du pro Account auswählen, welche
          Plattformen für jeden Post aktiv sein sollen.
        </p>
      ),
    },
    {
      q: 'Wie viele Videos sind im Plan enthalten?',
      a: (
        <>
          <p>
            <strong>Free:</strong> 5 Videos zum Ausprobieren, alle Templates
            inklusive Premium.<br />
            <strong>Starter (€29 statt €49 / Monat):</strong> 5 Videos pro Monat,
            alle Templates, Inserate-Import aus Justimmo &amp; Willhaben,
            ohne Auto-Posting.<br />
            <strong>Autopilot (€99 / Monat):</strong> Unbegrenzte Videos plus
            tägliches Auto-Posting, ein Makler-Account.<br />
            <strong>Team (€179 / Monat, ab 2 Nutzern):</strong> Unbegrenzte
            Videos pro Mitarbeiter, +€89 pro zusätzlichem Nutzer.
          </p>
          <p>
            Im Autopilot-Plan ist die Standard-Frequenz 1 Post pro Tag. Du kannst
            das in den Einstellungen aber höher setzen, wenn deine
            Inserate-Pipeline genug Material hergibt.
          </p>
        </>
      ),
    },
    {
      q: 'Was passiert mit meinen Immobiliendaten?',
      a: (
        <>
          <p>
            Wir speichern deine Inserate (Adressen, Beschreibungen, Bilder,
            Preise) für die Dauer deines Abonnements. Datenverarbeitung
            erfolgt ausschließlich in Rechenzentren in der EU. Inaktive
            Immobilien (nicht synchronisiert oder beworben in den letzten
            6 Monaten) werden automatisch gelöscht.
          </p>
          <p>
            Bei Kontolöschung verschwinden alle Daten endgültig nach einer
            7-tägigen Karenzzeit. Volle Details findest du in der{' '}
            <Link to="/datenschutz" className="text-[#1f4d3a] underline">
              Datenschutzerklärung
            </Link>.
          </p>
        </>
      ),
    },
    {
      q: 'Kann ich Posts vor der Veröffentlichung kontrollieren?',
      a: (
        <>
          <p>
            Ja. Standardmäßig schicken wir dir 12 Stunden vor jedem
            Auto-Post eine Vorschau-E-Mail. In dieser Voreinstellung
            („Nur benachrichtigen") posten wir automatisch zur geplanten
            Zeit. Du musst nur reagieren, wenn etwas nicht passt:
            „Stoppen" überspringt den Post, „Jetzt veröffentlichen"
            postet sofort.
          </p>
          <p>
            Möchtest du jeden Post aktiv freigeben? In den Auto-Post-
            Einstellungen kannst du auf „Manuelle Freigabe" umschalten –
            dann posten wir nur, wenn du in der Vorschau-E-Mail aktiv
            auf „Freigeben" klickst. Oder auf „Komplett automatisch",
            wenn du gar keine Mails mehr willst.
          </p>
        </>
      ),
    },
    {
      q: 'Kann ich meine eigene Stimme verwenden?',
      a: (
        <>
          <p>
            Du wählst aus 6 kuratierten Stimmen aus (Matilda, Rachel,
            Charlotte, Antoni, Liam, Daniel). Alle laufen über das
            ElevenLabs-Multilingual-Modell und sprechen sauberes Deutsch.
            Im Onboarding hörst du jede Stimme mit deinem Demo-Listing,
            damit du A/B vergleichen kannst.
          </p>
          <p>
            Wenn du bei ElevenLabs deine eigene Stimme geklont hast, kannst
            du im Voice-Picker unter „Erweitert" die voice_id eingeben. Die
            verwenden wir dann für deine Auto-Posts.
          </p>
        </>
      ),
    },
    {
      q: 'Was kostet die Testversion?',
      a: (
        <>
          <p>
            Die Free-Version ist kostenlos und beinhaltet 5 Videos pro
            Monat. Es wird <strong>nichts abgebucht</strong>.
          </p>
          <p>
            Wir bitten dich allerdings vor der ersten Erstellung eines
            Videos um zwei Verifizierungsschritte: deine E-Mail-Adresse
            bestätigen und eine Kreditkarte für eine €0-Authentifizierung
            hinterlegen. Damit verhindern wir Missbrauch durch
            Wegwerf-Accounts. Die Karte wird ausschließlich verifiziert,
            niemals belastet.
          </p>
        </>
      ),
    },
    {
      q: 'Wie kündige ich?',
      a: (
        <p>
          Über das <strong>Stripe Customer Portal</strong>. Den Link
          findest du im Bereich „Abrechnung verwalten" auf der
          Pricing-Seite. Klick „Subscription kündigen" und du behältst
          den Pro-Zugang noch bis zum Ende des bezahlten Zeitraums.
          Erstattungen für nicht genutzte Restzeiten gibt es laut
          unseren AGB nicht (Standard bei SaaS-Abos), aber das Abonnement
          läuft sauber aus.
        </p>
      ),
    },
    {
      q: 'Gibt es einen Team-Plan?',
      a: (
        <>
          <p>
            <strong>Bald</strong>. Der Team-Plan startet mit 2 Nutzern für
            €179/Monat, jeder weitere Mitarbeiter kostet zusätzlich
            €89/Monat. Wenn ihr ein Maklerbüro mit mehreren Agents seid und
            jeder seine eigenen Listings via Justimmo hat, ist das die
            richtige Lösung. Ein Account, eine Abrechnung, jeder Makler
            postet nur seine eigenen Inserate.
          </p>
          <p>
            Schreib uns an{' '}
            <a className="text-[#1f4d3a] underline" href="mailto:support@real-content.at">
              support@real-content.at
            </a>
            {' '}wenn du Interesse hast. Wir benachrichtigen dich, sobald
            der Plan live ist.
          </p>
        </>
      ),
    },
  ],
  en: [
    {
      q: 'How does Real Content work?',
      a: (
        <>
          <p>
            You connect your Justimmo account once. We import your active
            listings and you pick the templates you like. Then we post
            automatically. One video per day, fully generated with AI
            voiceover, music, and your branding.
          </p>
          <p>
            You don't have to do anything: a new listing in Justimmo? We
            create a social-media video from it within 12 hours, send you
            a preview, and post at the scheduled time.
          </p>
        </>
      ),
    },
    {
      q: 'Do I need a Justimmo account?',
      a: (
        <>
          <p>
            Currently yes. Real Content fetches your real-estate listings
            via the Justimmo Export API. That's what makes auto-posting
            work without extra manual upload.
          </p>
          <p>
            Other systems (immowelt, ImmoScout24 Manager, etc.) aren't
            supported directly yet. If you use one of those and want to
            try Real Content anyway, drop us a line at{' '}
            <a className="text-[#1f4d3a] underline" href="mailto:support@real-content.at">
              support@real-content.at
            </a>
            {' '}– we'll find a way.
          </p>
        </>
      ),
    },
    {
      q: 'Which social-media platforms are supported?',
      a: (
        <p>
          Six out of the box: <strong>Instagram, Facebook, TikTok, YouTube
          (Shorts), X (Twitter)</strong>, and <strong>LinkedIn</strong>.
          You connect each once via Upload-Post, then we post in parallel.
          In the auto-post settings you can pick per account which platforms
          should be active for each post.
        </p>
      ),
    },
    {
      q: 'How many videos are included in each plan?',
      a: (
        <>
          <p>
            <strong>Free:</strong> 5 videos to try it out, all templates
            including Premium.<br />
            <strong>Starter (€29 instead of €49 / month):</strong> 5 videos per month,
            all templates, listing import from Justimmo &amp; Willhaben,
            no auto-posting.<br />
            <strong>Autopilot (€99 / month):</strong> unlimited videos plus daily
            auto-posting, single agent account.<br />
            <strong>Team (€179 / month, starting at 2 users):</strong>{' '}
            unlimited per team member, +€89 per additional user.
          </p>
          <p>
            On Autopilot the default cadence is 1 post per day. You can crank
            that up in settings if your listing pipeline produces enough
            material.
          </p>
        </>
      ),
    },
    {
      q: 'What happens to my listing data?',
      a: (
        <>
          <p>
            We store your listings (addresses, descriptions, images, prices)
            for the duration of your subscription. Data is processed only
            in EU data centers. Inactive listings (not synced or posted
            for 6+ months) are automatically deleted.
          </p>
          <p>
            On account deletion, all data is permanently removed after a
            7-day grace period. Full details in our{' '}
            <Link to="/datenschutz" className="text-[#1f4d3a] underline">
              Privacy Policy
            </Link>.
          </p>
        </>
      ),
    },
    {
      q: 'Can I control posts before they go live?',
      a: (
        <>
          <p>
            Yes. We send a preview email 12 hours before every auto-post.
            On the default setting ("Notify only") we post automatically
            at the scheduled time; you only have to react if something
            looks off. "Stop" skips that post, "Publish now" posts
            immediately.
          </p>
          <p>
            Want to actively approve each one? Switch to "Manual approval"
            in auto-post settings. Then we only post when you click
            "Approve" in the preview email. Or "Fully automatic" if you
            want no emails at all.
          </p>
        </>
      ),
    },
    {
      q: 'Can I use my own voice?',
      a: (
        <>
          <p>
            You pick from 6 curated voices (Matilda, Rachel, Charlotte,
            Antoni, Liam, Daniel). All running on ElevenLabs' multilingual
            model and producing clean German. In onboarding you hear each
            voice on your demo listing so you can A/B compare.
          </p>
          <p>
            If you've cloned your own voice on ElevenLabs, you can paste
            the voice_id into the picker's "Advanced" field. We use it
            for your auto-posts.
          </p>
        </>
      ),
    },
    {
      q: 'What does the trial cost?',
      a: (
        <>
          <p>
            The Free version is free and includes 5 videos per month.{' '}
            <strong>Nothing is charged.</strong>
          </p>
          <p>
            Before your first video, we ask for two verification steps:
            confirm your email and add a credit card for €0 authorization.
            That stops abuse from throwaway accounts. The card is only
            verified, never charged.
          </p>
        </>
      ),
    },
    {
      q: 'How do I cancel?',
      a: (
        <p>
          Via the <strong>Stripe Customer Portal</strong>. Find the link
          under "Manage subscription" on the Pricing page. Click "Cancel
          subscription" and you keep Pro access until the end of your
          paid period. Per our Terms there are no refunds for unused time
          (standard SaaS), but the subscription cleanly winds down.
        </p>
      ),
    },
    {
      q: 'Is there a team plan?',
      a: (
        <>
          <p>
            <strong>Soon</strong>. The Team plan starts at 2 users for
            €179/month, each additional team member is €89/month. If
            you're an agency with multiple agents and each has their own
            Justimmo listings, this is the right fit. One account, one
            invoice, each agent only posts their own listings.
          </p>
          <p>
            Email{' '}
            <a className="text-[#1f4d3a] underline" href="mailto:support@real-content.at">
              support@real-content.at
            </a>
            {' '}if you're interested. We'll let you know when it goes live.
          </p>
        </>
      ),
    },
  ],
};

// Strip JSX from an answer so Google's FAQPage schema gets plain text.
// JSX answers contain <p>, <Link>, <strong> elements. Schema wants
// the rendered string. We do a quick render-to-string by walking the
// React tree manually rather than depending on react-dom/server.
function jsxToText(node) {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(jsxToText).join('');
  if (node && node.props && node.props.children !== undefined) {
    return jsxToText(node.props.children);
  }
  return '';
}

export default function FAQPage() {
  const { lang } = useTranslation();
  useDocumentTitle(
    lang === 'en'
      ? 'FAQ. Real Content | Real estate video automation answers'
      : 'FAQ. Real Content | Antworten zu Immobilien-Video-Automatisierung'
  );
  const items = FAQ_ITEMS[lang] || FAQ_ITEMS.de;

  // SEO. Inject FAQPage schema so Google can show rich-result FAQ
  // snippets in search results. Rendered into a <script type="ld+json">
  // tag in document.head and cleaned up on unmount. Massive CTR win:
  // FAQ snippets in SERP look like the SERP showed an answer card, not
  // just a link.
  React.useEffect(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: items.map((item) => ({
        '@type': 'Question',
        name: item.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: jsxToText(item.a).trim(),
        },
      })),
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    script.dataset.injected = 'faqpage';
    document.head.appendChild(script);
    return () => {
      script.remove();
    };
  }, [items]);

  return (
    <div className="min-h-screen bg-[#f7f3ea]">
      <header className="border-b border-[#e3dccb] backdrop-blur-xl bg-[#f7f3ea]/85">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-[#4a463b] hover:text-[#23211a]">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">{lang === 'en' ? 'Back' : 'Zurück'}</span>
          </Link>
          <Link to="/" className="flex items-center gap-2 text-[#23211a] hover:text-[#23211a]">
            <Home className="w-5 h-5 text-[#1f4d3a]" />
            <span className="font-semibold">Real Content</span>
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-10">
        <div className="text-center mb-10">
          <div className="inline-flex p-3 bg-[#e9efe7] rounded-2xl mb-3">
            <HelpCircle className="w-7 h-7 text-[#1f4d3a]" />
          </div>
          <h1 className="text-3xl font-bold text-[#23211a] mb-2">
            {lang === 'en' ? 'Frequently asked questions' : 'Häufig gestellte Fragen'}
          </h1>
          <p className="text-sm text-[#6f6a5c]">
            {lang === 'en'
              ? 'The 10 things people ask before signing up. If yours isn’t here, email us.'
              : 'Die 10 häufigsten Fragen vor dem Start. Was hier fehlt, beantworten wir per E-Mail.'}
          </p>
        </div>

        <div className="space-y-2">
          {items.map((item, i) => (
            <details
              key={i}
              className="group rounded-xl border border-[#e3dccb] bg-[#f2ecdf] [&[open]]:border-[#1f4d3a]/40 [&[open]]:bg-[#f0f3ec] transition-colors"
            >
              <summary className="cursor-pointer list-none flex items-start justify-between gap-3 p-4 sm:p-5">
                <h2 className="text-base sm:text-lg font-medium text-[#23211a] m-0">
                  {item.q}
                </h2>
                <ChevronDown className="w-5 h-5 text-[#8a8474] mt-0.5 shrink-0 transition-transform group-open:rotate-180" />
              </summary>
              <div className="px-4 sm:px-5 pb-4 sm:pb-5 -mt-1 text-sm text-[#4a463b] leading-relaxed [&>p+p]:mt-3 [&_strong]:text-[#23211a]">
                {item.a}
              </div>
            </details>
          ))}
        </div>

        <div className="mt-12 text-center text-sm text-[#6f6a5c]">
          {lang === 'en' ? (
            <>
              Question we missed?{' '}
              <a className="text-[#1f4d3a] underline" href="mailto:support@real-content.at">
                Email us
              </a>
              {' '}– we usually answer within a day.
            </>
          ) : (
            <>
              Frage nicht dabei?{' '}
              <a className="text-[#1f4d3a] underline" href="mailto:support@real-content.at">
                Schreib uns
              </a>
              {' '}– wir antworten meistens innerhalb eines Tages.
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
